import React, { useMemo } from 'react';
import { TrendingDown, Sprout } from 'lucide-react';
import { Holding } from '../../types';
import { useCurrency } from '../../context/CurrencyContext';
import { InfoTooltip } from '../ui/InfoTooltip';

interface Props {
  holdings: Holding[];
  /** Marginal rate used to estimate tax savings — taken from saved estimates, or a default. */
  bracketRate: number;
  /** Open the Planned Sales "Add estimate" panel prefilled with this coin. */
  onAddToPlan: (coinId: string) => void;
}

function CoinBadge({ image, ticker }: { image?: string; ticker: string }) {
  if (image) {
    return <img src={image} alt={ticker} className="w-7 h-7 rounded-full flex-shrink-0 bg-slate-800" />;
  }
  return (
    <div className="w-7 h-7 rounded-full flex-shrink-0 bg-slate-700 flex items-center justify-center">
      <span className="text-[10px] font-semibold text-slate-300">{ticker.slice(0, 3)}</span>
    </div>
  );
}

export function HarvestOpportunities({ holdings, bracketRate, onAddToPlan }: Props) {
  const { fmt } = useCurrency();

  // A position is harvestable when it's currently sitting on an unrealised loss.
  // Selling it would realise that loss, which can offset capital gains elsewhere.
  const losses = useMemo(
    () => holdings.filter((h) => h.amount > 0 && h.pnl < 0).sort((a, b) => a.pnl - b.pnl),
    [holdings]
  );

  return (
    <div>
      {/* Subsection header */}
      <div className="flex items-center gap-1.5 mb-1">
        <p
          className="uppercase"
          style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', color: 'var(--color-bronze)' }}
        >
          Harvest Opportunities
        </p>
        <InfoTooltip text="Tax-loss harvesting means selling an asset that's currently worth less than you paid, to realise a capital loss. That loss offsets your capital gains, lowering your overall CGT. This list is generated automatically from coins you hold that are below their average buy price." />
      </div>
      <h3 className="text-slate-100" style={{ fontSize: 16, fontWeight: 500 }}>
        Reduce your tax bill by harvesting losses
      </h3>
      <p className="text-slate-400 mt-1.5 mb-4" style={{ fontSize: 13 }}>
        Coins in your portfolio currently at a loss. Selling at a loss can offset your capital gains and reduce the tax you owe. These are suggestions based on your live holdings - not saved estimates.
      </p>

      {losses.length === 0 ? (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-[rgb(var(--overlay)/0.02)] border border-slate-700/50">
          <Sprout className="w-4 h-4 text-slate-500 flex-shrink-0" />
          <p className="text-[13px] text-slate-500">
            No harvestable losses right now. Your unrealized positions are all positive.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {losses.map((h) => {
            const loss = Math.abs(h.pnl);
            const savings = loss * bracketRate;
            return (
              <div
                key={h.coinId}
                className="flex items-center gap-4 p-4 rounded-xl border border-slate-700/60 transition-colors hover:bg-[rgb(var(--overlay)/0.03)]"
              >
                {/* Identity */}
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <CoinBadge image={undefined} ticker={h.symbol.toUpperCase()} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-100 truncate">
                      {h.name} <span className="text-slate-500 font-normal">{h.symbol.toUpperCase()}</span>
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {h.amount.toLocaleString('en-US', { maximumFractionDigits: 6 })} held
                    </p>
                  </div>
                </div>

                {/* Unrealised loss */}
                <div className="hidden sm:block text-right">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Unrealized loss</p>
                  <p className="text-sm font-medium text-red-400 tabular-nums inline-flex items-center gap-1">
                    <TrendingDown className="w-3.5 h-3.5" />
                    −{fmt(loss)}
                  </p>
                </div>

                {/* Estimated tax saving */}
                <div className="hidden md:block text-right">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Est. tax saving</p>
                  <p className="text-sm font-medium text-emerald-400 tabular-nums">+{fmt(savings)}</p>
                </div>

                {/* Add to plan */}
                <button
                  type="button"
                  onClick={() => onAddToPlan(h.coinId)}
                  className="flex-shrink-0 text-[13px] font-medium px-3.5 py-2 rounded-lg border border-slate-600 text-slate-200 hover:bg-slate-700/50 hover:border-slate-500 transition-colors whitespace-nowrap"
                >
                  Add to plan
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
